import React, { useState, useEffect } from 'react';
import {
  Shield, Target, AlertTriangle, FileText,
  ClipboardCheck, BarChart3, Plus, Edit2, Trash2, Check,
  X, ChevronDown, ChevronUp, Eye, Save, Download,
  FolderOpen, Star, BookOpen, TrendingUp,
} from 'lucide-react';
import { qmApi } from '../services/api';
import { useToast } from '../context/ToastContext';

type Tab = 'dashboard' | 'policy' | 'risks' | 'documents' | 'ablage' | 'audits' | 'reviews' | 'nonconformities';

// Mapping: doc_number prefix -> form template type
const DOC_TO_FORM_TYPE: Record<string, string> = {
  'QM-FB-001': 'feedback', 'QM-FB-002': 'audit', 'QM-FB-003': 'complaint',
};

// PDCA-grouped navigation
const NAV_GROUPS: { label: string; color: string; tabs: { key: Tab; label: string; icon: React.ReactNode }[] }[] = [
  {
    label: '', color: '',
    tabs: [{ key: 'dashboard', label: 'Überblick', icon: <BarChart3 size={15} /> }],
  },
  {
    label: 'PLANEN', color: '#3b82f6',
    tabs: [
      { key: 'policy', label: 'Politik & Ziele', icon: <Shield size={15} /> },
      { key: 'risks', label: 'Risiken', icon: <AlertTriangle size={15} /> },
    ],
  },
  {
    label: 'DURCHFÜHREN', color: '#10b981',
    tabs: [
      { key: 'documents', label: 'Dokumente', icon: <FileText size={15} /> },
      { key: 'ablage', label: 'Formulare', icon: <FolderOpen size={15} /> },
    ],
  },
  {
    label: 'PRÜFEN', color: '#f59e0b',
    tabs: [
      { key: 'audits', label: 'Audits', icon: <ClipboardCheck size={15} /> },
      { key: 'reviews', label: 'Bewertungen', icon: <BookOpen size={15} /> },
    ],
  },
  {
    label: 'HANDELN', color: '#ef4444',
    tabs: [
      { key: 'nonconformities', label: 'CAPA', icon: <TrendingUp size={15} /> },
    ],
  },
];

export default function QualityManagement() {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [pendingFormType, setPendingFormType] = useState<string | null>(null);
  const [pendingCapa, setPendingCapa] = useState<any>(null);

  const fillForm = (docNumber: string) => {
    const formType = DOC_TO_FORM_TYPE[docNumber];
    if (formType) {
      setPendingFormType(formType);
      setActiveTab('ablage');
    }
  };

  const createCapaFromRisk = (risk: any) => {
    setPendingCapa(risk);
    setActiveTab('nonconformities');
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
            <Shield size={22} style={{ color: 'var(--primary)' }} />
            <h1 style={{ fontSize: '1.4rem', fontWeight: 700, margin: 0 }}>Qualitätsmanagement</h1>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0, paddingLeft: 32 }}>
            ISO 9001:2015 | PDCA-Zyklus | AC-Leadership Consulting
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 24, flexWrap: 'wrap' }}>
        {NAV_GROUPS.flatMap(g => g.tabs).map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '8px 12px', border: 'none', cursor: 'pointer',
              fontSize: '0.84rem', fontWeight: activeTab === t.key ? 600 : 400,
              color: activeTab === t.key ? 'var(--primary)' : 'var(--text-light)',
              background: 'none',
              borderBottom: activeTab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
              marginBottom: -1, transition: 'color 0.15s',
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && <QMDashboard onNavigate={setActiveTab} />}
      {activeTab === 'policy' && <PolicyObjectivesTab />}
      {activeTab === 'risks' && <RisksTab onCreateCapa={createCapaFromRisk} />}
      {activeTab === 'documents' && <DocumentsTab onFillForm={fillForm} />}
      {activeTab === 'ablage' && <AblageTab initialFormType={pendingFormType} onConsumed={() => setPendingFormType(null)} />}
      {activeTab === 'audits' && <AuditsTab />}
      {activeTab === 'reviews' && <ReviewsTab />}
      {activeTab === 'nonconformities' && <NonconformitiesTab initialRisk={pendingCapa} onConsumed={() => setPendingCapa(null)} />}
    </div>
  );
}

/* ──────────────── Shared Styles ──────────────── */

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border)',
  padding: 20, marginBottom: 16,
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', background: 'var(--primary)', color: 'white',
  border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
};

const btnSecondary: React.CSSProperties = {
  ...btnPrimary, background: 'var(--bg)', color: 'var(--text)',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid var(--border)',
  borderRadius: 6, fontSize: '0.85rem', background: 'var(--bg-card)', color: 'var(--text)',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle, minHeight: 80, fontFamily: 'inherit', resize: 'vertical' as const,
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.8rem', fontWeight: 500,
  color: 'var(--text-light)', marginBottom: 4,
};

const helpBox: React.CSSProperties = {
  background: 'var(--border-light)', border: '1px solid var(--border)', borderRadius: 8,
  padding: '12px 16px', fontSize: '0.84rem', color: 'var(--text-light)', lineHeight: 1.6, marginBottom: 16,
};

const badge = (color: string): React.CSSProperties => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 12,
  fontSize: '0.75rem', fontWeight: 500, background: color + '20', color,
});

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    open: '#f59e0b', in_progress: '#3b82f6', achieved: '#10b981', missed: '#ef4444',
    identified: '#6b7280', assessed: '#f59e0b', mitigated: '#10b981', closed: '#6b7280',
    draft: '#6b7280', review: '#f59e0b', approved: '#10b981', archived: '#6b7280',
    analysis: '#f59e0b', corrective_action: '#3b82f6', verification: '#8b5cf6',
    planned: '#3b82f6', completed: '#10b981', cancelled: '#ef4444',
    minor: '#f59e0b', major: '#ef4444', critical: '#dc2626',
  };
  const labels: Record<string, string> = {
    open: 'Offen', in_progress: 'In Bearbeitung', achieved: 'Erreicht', missed: 'Verfehlt',
    identified: 'Identifiziert', assessed: 'Bewertet', mitigated: 'Gemildert', closed: 'Geschlossen',
    draft: 'Entwurf', review: 'Prüfung', approved: 'Freigegeben', archived: 'Archiviert',
    analysis: 'Analyse', corrective_action: 'Korrektur', verification: 'Verifizierung',
    planned: 'Geplant', completed: 'Abgeschlossen', cancelled: 'Abgebrochen',
    minor: 'Gering', major: 'Erheblich', critical: 'Kritisch',
  };
  return <span style={badge(colors[status] || '#6b7280')}>{labels[status] || status}</span>;
}

/* ──────────────── Dashboard ──────────────── */

function QMDashboard({ onNavigate }: { onNavigate: (tab: Tab) => void }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    qmApi.dashboard().then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Wird geladen...</p>;
  if (!data) return <p>Fehler beim Laden.</p>;

  const pdcaSections: { phase: string; color: string; icon: React.ReactNode; items: { label: string; value: string | number; status: 'ok' | 'warn' | 'error' | 'info'; tab: Tab }[] }[] = [
    {
      phase: 'PLANEN', color: '#3b82f6', icon: <Target size={18} />,
      items: [
        { label: 'Qualitätspolitik', value: data.policy_approved ? 'Freigegeben' : data.policy_exists ? 'Entwurf' : 'Fehlt', status: data.policy_approved ? 'ok' : 'warn', tab: 'policy' },
        { label: 'Ziele erreicht', value: `${data.objectives.achieved} / ${data.objectives.total}`, status: data.objectives.achieved === data.objectives.total && data.objectives.total > 0 ? 'ok' : 'info', tab: 'policy' },
        { label: 'Risiken (hoch)', value: data.risks.high, status: data.risks.high > 0 ? 'error' : 'ok', tab: 'risks' },
      ],
    },
    {
      phase: 'DURCHFÜHREN', color: '#10b981', icon: <FileText size={18} />,
      items: [
        { label: 'Dokumente gesamt', value: data.documents.total, status: 'info', tab: 'documents' },
        { label: 'Prüfung fällig', value: data.documents.review_due, status: data.documents.review_due > 0 ? 'error' : 'ok', tab: 'documents' },
      ],
    },
    {
      phase: 'PRÜFEN', color: '#f59e0b', icon: <ClipboardCheck size={18} />,
      items: [
        { label: 'Audits geplant', value: data.audits.planned, status: data.audits.planned > 0 ? 'info' : 'warn', tab: 'audits' },
        { label: 'Audits abgeschlossen', value: data.audits.completed || 0, status: 'info', tab: 'audits' },
        { label: 'Managementbewertungen', value: data.management_reviews.total, status: data.management_reviews.total > 0 ? 'ok' : 'warn', tab: 'reviews' },
      ],
    },
    {
      phase: 'HANDELN', color: '#ef4444', icon: <TrendingUp size={18} />,
      items: [
        { label: 'Offene CAPA', value: data.nonconformities.open, status: data.nonconformities.open > 0 ? 'warn' : 'ok', tab: 'nonconformities' },
        { label: 'Abgeschlossen', value: (data.nonconformities.total || 0) - (data.nonconformities.open || 0), status: 'info', tab: 'nonconformities' },
      ],
    },
  ];

  const statusColors = { ok: '#10b981', warn: '#f59e0b', error: '#ef4444', info: '#6b7280' };

  const checklist = [
    { label: 'Qualitätspolitik definiert & freigegeben', done: data.policy_approved, chapter: 'Kap. 5' },
    { label: 'Qualitätsziele festgelegt', done: data.objectives.total > 0, chapter: 'Kap. 6' },
    { label: 'Risiken & Chancen bewertet', done: data.risks.total > 0, chapter: 'Kap. 6' },
    { label: 'Dokumente gelenkt & freigegeben', done: data.documents.total > 0, chapter: 'Kap. 7' },
    { label: 'Beschwerdemanagement eingerichtet', done: true, chapter: 'Kap. 10' },
    { label: 'Interne Audits geplant', done: data.audits.total > 0, chapter: 'Kap. 9.2' },
    { label: 'Managementbewertung durchgeführt', done: data.management_reviews.total > 0, chapter: 'Kap. 9.3' },
  ];
  const checkDone = checklist.filter(c => c.done).length;
  const checkTotal = checklist.length;

  return (
    <div>
      {/* PDCA Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {pdcaSections.map(sec => (
          <div key={sec.phase} style={{ ...cardStyle, marginBottom: 0, borderTop: `3px solid ${sec.color}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              <span style={{ color: sec.color }}>{sec.icon}</span>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: sec.color, letterSpacing: '0.06em' }}>{sec.phase}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sec.items.map((item, j) => (
                <div
                  key={j}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => onNavigate(item.tab)}
                >
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-light)' }}>{item.label}</span>
                  <span style={{ fontSize: '1rem', fontWeight: 700, color: statusColors[item.status] }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ISO Checklist */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>ISO 9001:2015 Anforderungen</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 120, height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
              <div style={{ width: `${(checkDone / checkTotal) * 100}%`, height: '100%', background: 'var(--success)', borderRadius: 3, transition: 'width 0.3s' }} />
            </div>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: checkDone === checkTotal ? 'var(--success)' : 'var(--text-muted)' }}>
              {checkDone}/{checkTotal}
            </span>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {checklist.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 4, background: item.done ? 'var(--border-light)' : 'transparent' }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                background: item.done ? 'var(--success)' : 'var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {item.done && <Check size={12} color="white" />}
              </div>
              <span style={{ fontSize: '0.82rem', color: item.done ? 'var(--text)' : 'var(--text-muted)', flex: 1 }}>{item.label}</span>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{item.chapter}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Politik & Ziele (combined) ──────────────── */

function PolicyObjectivesTab() {
  const [subTab, setSubTab] = useState<'policy' | 'objectives'>('policy');
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { key: 'policy' as const, label: 'Qualitätspolitik', icon: <Shield size={14} /> },
          { key: 'objectives' as const, label: 'Qualitätsziele', icon: <Target size={14} /> },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 14px', borderRadius: 20, border: '1px solid var(--border)',
              fontSize: '0.84rem', fontWeight: subTab === t.key ? 600 : 400, cursor: 'pointer',
              background: subTab === t.key ? 'var(--primary)' : 'var(--bg-card)',
              color: subTab === t.key ? 'white' : 'var(--text-light)',
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      {subTab === 'policy' && <PolicySection />}
      {subTab === 'objectives' && <ObjectivesSection />}
    </div>
  );
}

function PolicySection() {
  const [policy, setPolicy] = useState({ vision: '', mission: '', values: '', policy_text: '', approved_at: null as string | null, approved_by: null as string | null, version: 1 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    qmApi.getPolicy().then(r => { setPolicy(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const r = await qmApi.savePolicy({ vision: policy.vision, mission: policy.mission, values: policy.values, policy_text: policy.policy_text });
      setPolicy(r.data);
      setMsg('Gespeichert');
    } catch { setMsg('Fehler beim Speichern'); }
    setSaving(false);
  };

  const approve = async () => {
    try {
      const r = await qmApi.approvePolicy();
      setPolicy(r.data);
      setMsg('Freigegeben');
    } catch { setMsg('Fehler'); }
  };

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Wird geladen...</p>;

  return (
    <div>
      <div style={helpBox}>
        <strong>Was ist die Qualitätspolitik?</strong> Hier beschreiben Sie die Grundwerte und Ausrichtung Ihres Coachings.
        Die <em>Vision</em> ist Ihr langfristiges Zielbild, die <em>Mission</em> beschreibt, wie Sie dorthin gelangen.
        Unter <em>Werte</em> notieren Sie, was Ihnen in der Zusammenarbeit wichtig ist.
        Die <em>Qualitätspolitik</em> fasst Ihr Qualitätsversprechen zusammen — z.B. „Wir verpflichten uns zu vertraulicher, wissenschaftlich fundierter Begleitung."
        Nach dem Ausfüllen klicken Sie auf „Freigeben", damit das Dokument offiziell gilt.
      </div>
      {policy.approved_at && (
        <div style={{ background: 'var(--border-light)', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: '0.85rem', color: 'var(--success)' }}>
          Freigegeben am {new Date(policy.approved_at).toLocaleDateString('de-DE')} von {policy.approved_by} (Version {policy.version})
        </div>
      )}
      <div style={cardStyle}>
        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <label style={labelStyle}>Vision</label>
            <textarea style={textareaStyle} value={policy.vision} onChange={e => setPolicy({ ...policy, vision: e.target.value })} placeholder="Unsere Vision..." />
          </div>
          <div>
            <label style={labelStyle}>Mission</label>
            <textarea style={textareaStyle} value={policy.mission} onChange={e => setPolicy({ ...policy, mission: e.target.value })} placeholder="Unsere Mission..." />
          </div>
          <div>
            <label style={labelStyle}>Werte</label>
            <textarea style={textareaStyle} value={policy.values} onChange={e => setPolicy({ ...policy, values: e.target.value })} placeholder="Unsere Werte..." />
          </div>
          <div>
            <label style={labelStyle}>Qualitätspolitik</label>
            <textarea style={{ ...textareaStyle, minHeight: 120 }} value={policy.policy_text} onChange={e => setPolicy({ ...policy, policy_text: e.target.value })} placeholder="Wir verpflichten uns..." />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <button style={btnPrimary} onClick={save} disabled={saving}>
            <Save size={14} /> {saving ? 'Speichert...' : 'Speichern'}
          </button>
          <button style={btnSecondary} onClick={approve}>
            <Check size={14} /> Freigeben
          </button>
          {msg && <span style={{ fontSize: '0.85rem', color: 'var(--success)' }}>{msg}</span>}
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Objectives ──────────────── */

function ObjectivesSection() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', description: '', target_value: '', current_value: '', unit: '', deadline: '', status: 'open', responsible: '', measures: '' });

  const load = () => { qmApi.listObjectives().then(r => { setItems(r.data.items || r.data); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, []);

  const resetForm = () => { setForm({ title: '', description: '', target_value: '', current_value: '', unit: '', deadline: '', status: 'open', responsible: '', measures: '' }); setEditId(null); setShowForm(false); };

  const save = async () => {
    const payload = { ...form, deadline: form.deadline || null };
    if (editId) { await qmApi.updateObjective(editId, payload); } else { await qmApi.createObjective(payload); }
    resetForm(); load();
  };

  const startEdit = (item: any) => {
    setForm({ title: item.title || '', description: item.description || '', target_value: item.target_value || '', current_value: item.current_value || '', unit: item.unit || '', deadline: item.deadline ? item.deadline.split('T')[0] : '', status: item.status, responsible: item.responsible || '', measures: item.measures || '' });
    setEditId(item.id); setShowForm(true);
  };

  const del = async (id: string) => { if (window.confirm('Ziel löschen?')) { await qmApi.deleteObjective(id); load(); } };

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Wird geladen...</p>;

  return (
    <div>
      <div style={helpBox}>
        <strong>Qualitätsziele definieren:</strong> Legen Sie messbare Ziele fest, an denen Sie Ihren Coaching-Erfolg messen.
        Beispiele: „Kundenzufriedenheit &ge; 4,5/5", „Alle Sitzungen innerhalb von 48h dokumentiert" oder „Mindestens 2 Fortbildungen pro Jahr".
        Geben Sie einen <em>Zielwert</em> und <em>Istwert</em> an, damit Sie den Fortschritt verfolgen können.
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>{items.length} Qualitätsziele</span>
        <button style={btnPrimary} onClick={() => { resetForm(); setShowForm(true); }}><Plus size={14} /> Neues Ziel</button>
      </div>

      {showForm && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 12 }}>{editId ? 'Ziel bearbeiten' : 'Neues Ziel'}</h3>
          <div style={{ display: 'grid', gap: 12 }}>
            <div><label style={labelStyle}>Titel *</label><input style={inputStyle} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
            <div><label style={labelStyle}>Beschreibung</label><textarea style={textareaStyle} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Zielwert</label><input style={inputStyle} value={form.target_value} onChange={e => setForm({ ...form, target_value: e.target.value })} /></div>
              <div><label style={labelStyle}>Istwert</label><input style={inputStyle} value={form.current_value} onChange={e => setForm({ ...form, current_value: e.target.value })} /></div>
              <div><label style={labelStyle}>Einheit</label><input style={inputStyle} value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder="z.B. %, Stk." /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Frist</label><input style={inputStyle} type="date" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} /></div>
              <div><label style={labelStyle}>Verantwortlich</label><input style={inputStyle} value={form.responsible} onChange={e => setForm({ ...form, responsible: e.target.value })} /></div>
              <div><label style={labelStyle}>Status</label>
                <select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="open">Offen</option><option value="in_progress">In Bearbeitung</option>
                  <option value="achieved">Erreicht</option><option value="missed">Verfehlt</option>
                </select>
              </div>
            </div>
            <div><label style={labelStyle}>Maßnahmen</label><textarea style={textareaStyle} value={form.measures} onChange={e => setForm({ ...form, measures: e.target.value })} /></div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={btnPrimary} onClick={save}><Save size={14} /> Speichern</button>
            <button style={btnSecondary} onClick={resetForm}><X size={14} /> Abbrechen</button>
          </div>
        </div>
      )}

      {items.map(item => (
        <div key={item.id} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{item.title}</div>
              {item.description && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>{item.description}</div>}
            </div>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <StatusBadge status={item.status} />
              <button style={{ ...btnSecondary, padding: '4px 8px' }} onClick={() => startEdit(item)}><Edit2 size={14} /></button>
              <button style={{ ...btnSecondary, padding: '4px 8px' }} onClick={() => del(item.id)}><Trash2 size={14} /></button>
            </div>
          </div>
          {(item.target_value || item.current_value) && (
            <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {item.target_value && <span>Ziel: {item.target_value} {item.unit}</span>}
              {item.current_value && <span>Ist: {item.current_value} {item.unit}</span>}
              {item.deadline && <span>Frist: {new Date(item.deadline).toLocaleDateString('de-DE')}</span>}
              {item.responsible && <span>Verantwortlich: {item.responsible}</span>}
            </div>
          )}
        </div>
      ))}
      {items.length === 0 && !showForm && <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 32 }}>Noch keine Qualitätsziele definiert.</p>}
    </div>
  );
}

/* ──────────────── Risks ──────────────── */

function RisksTab({ onCreateCapa }: { onCreateCapa: (risk: any) => void }) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', description: '', risk_type: 'risk', category: '', probability: 1, impact: 1, status: 'identified', mitigation: '', responsible: '', review_date: '' });

  const load = () => { qmApi.listRisks().then(r => { setItems(r.data.items || r.data); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, []);

  const resetForm = () => { setForm({ title: '', description: '', risk_type: 'risk', category: '', probability: 1, impact: 1, status: 'identified', mitigation: '', responsible: '', review_date: '' }); setEditId(null); setShowForm(false); };

  const save = async () => {
    const payload = { ...form, review_date: form.review_date || null };
    if (editId) { await qmApi.updateRisk(editId, payload); } else { await qmApi.createRisk(payload); }
    resetForm(); load();
  };

  const startEdit = (item: any) => {
    setForm({ title: item.title || '', description: item.description || '', risk_type: item.risk_type, category: item.category || '', probability: item.probability, impact: item.impact, status: item.status, mitigation: item.mitigation || '', responsible: item.responsible || '', review_date: item.review_date ? item.review_date.split('T')[0] : '' });
    setEditId(item.id); setShowForm(true);
  };

  const del = async (id: string) => { if (window.confirm('Eintrag löschen?')) { await qmApi.deleteRisk(id); load(); } };

  const riskColor = (score: number) => score >= 15 ? '#dc2626' : score >= 9 ? '#ef4444' : score >= 4 ? '#f59e0b' : '#10b981';

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Wird geladen...</p>;

  return (
    <div>
      <div style={helpBox}>
        <strong>Risiken & Chancen bewerten:</strong> Überlegen Sie, was in Ihrem Coaching-Alltag schiefgehen könnte (<em>Risiko</em>) oder welche Verbesserungsmöglichkeiten es gibt (<em>Chance</em>).
        Bewerten Sie jeweils <em>Wahrscheinlichkeit</em> (1-5) und <em>Auswirkung</em> (1-5) — daraus ergibt sich die Risikozahl.
        Ab einer Risikozahl von 9 sollten Sie Maßnahmen einplanen. Über den CAPA-Button können Sie direkt einen Maßnahmenplan ableiten.
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>{items.length} Risiken & Chancen</span>
        <button style={btnPrimary} onClick={() => { resetForm(); setShowForm(true); }}><Plus size={14} /> Neu</button>
      </div>

      {showForm && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 12 }}>{editId ? 'Bearbeiten' : 'Neu'}</h3>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Titel *</label><input style={inputStyle} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
              <div><label style={labelStyle}>Typ</label>
                <select style={inputStyle} value={form.risk_type} onChange={e => setForm({ ...form, risk_type: e.target.value })}>
                  <option value="risk">Risiko</option><option value="opportunity">Chance</option>
                </select>
              </div>
            </div>
            <div><label style={labelStyle}>Beschreibung</label><textarea style={textareaStyle} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Kategorie</label><input style={inputStyle} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="z.B. Prozess" /></div>
              <div><label style={labelStyle}>Wahrscheinlichkeit (1-5)</label><input style={inputStyle} type="number" min={1} max={5} value={form.probability} onChange={e => setForm({ ...form, probability: +e.target.value })} /></div>
              <div><label style={labelStyle}>Auswirkung (1-5)</label><input style={inputStyle} type="number" min={1} max={5} value={form.impact} onChange={e => setForm({ ...form, impact: +e.target.value })} /></div>
              <div><label style={labelStyle}>Risikozahl</label><div style={{ ...inputStyle, background: 'var(--bg)', fontWeight: 700, color: riskColor(form.probability * form.impact) }}>{form.probability * form.impact}</div></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Status</label>
                <select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="identified">Identifiziert</option><option value="assessed">Bewertet</option>
                  <option value="mitigated">Gemildert</option><option value="closed">Geschlossen</option>
                </select>
              </div>
              <div><label style={labelStyle}>Verantwortlich</label><input style={inputStyle} value={form.responsible} onChange={e => setForm({ ...form, responsible: e.target.value })} /></div>
              <div><label style={labelStyle}>Nächste Prüfung</label><input style={inputStyle} type="date" value={form.review_date} onChange={e => setForm({ ...form, review_date: e.target.value })} /></div>
            </div>
            <div><label style={labelStyle}>Maßnahmen</label><textarea style={textareaStyle} value={form.mitigation} onChange={e => setForm({ ...form, mitigation: e.target.value })} /></div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={btnPrimary} onClick={save}><Save size={14} /> Speichern</button>
            <button style={btnSecondary} onClick={resetForm}><X size={14} /> Abbrechen</button>
          </div>
        </div>
      )}

      {items.map(item => (
        <div key={item.id} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{item.title}</span>
                <span style={badge(item.risk_type === 'opportunity' ? '#10b981' : '#6b7280')}>{item.risk_type === 'opportunity' ? 'Chance' : 'Risiko'}</span>
                <StatusBadge status={item.status} />
              </div>
              {item.description && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>{item.description}</div>}
            </div>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: riskColor(item.risk_score) + '20', color: riskColor(item.risk_score), display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem' }}>
                {item.risk_score}
              </div>
              <button style={{ ...btnSecondary, padding: '4px 8px' }} onClick={() => onCreateCapa(item)} title="CAPA erstellen"><TrendingUp size={14} /></button>
              <button style={{ ...btnSecondary, padding: '4px 8px' }} onClick={() => startEdit(item)} title="Bearbeiten"><Edit2 size={14} /></button>
              <button style={{ ...btnSecondary, padding: '4px 8px' }} onClick={() => del(item.id)} title="Löschen"><Trash2 size={14} /></button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            <span>W: {item.probability} | A: {item.impact}</span>
            {item.category && <span>{item.category}</span>}
            {item.responsible && <span>Verantw.: {item.responsible}</span>}
          </div>
        </div>
      ))}
      {items.length === 0 && !showForm && <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 32 }}>Noch keine Risiken oder Chancen erfasst.</p>}
    </div>
  );
}

/* ──────────────── Documents ──────────────── */

function DocumentsTab({ onFillForm }: { onFillForm: (docNumber: string) => void }) {
  const { showToast } = useToast();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [viewDoc, setViewDoc] = useState<any | null>(null);
  const [form, setForm] = useState({ doc_number: '', title: '', description: '', category: 'procedure', version: '1.0', status: 'draft', content: '', approved_by: '', review_due: '', changes: '' });

  const load = () => { qmApi.listDocuments().then(r => { setItems(r.data.items || r.data); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, []);

  const resetForm = () => { setForm({ doc_number: '', title: '', description: '', category: 'procedure', version: '1.0', status: 'draft', content: '', approved_by: '', review_due: '', changes: '' }); setEditId(null); setShowForm(false); };

  const save = async () => {
    const payload = { ...form, review_due: form.review_due || null };
    if (editId) { await qmApi.updateDocument(editId, payload); } else { await qmApi.createDocument(payload); }
    resetForm(); load();
  };

  const startEdit = (item: any) => {
    setViewDoc(null);
    setForm({ doc_number: item.doc_number, title: item.title, description: item.description || '', category: item.category, version: item.version, status: item.status, content: item.content || '', approved_by: item.approved_by || '', review_due: item.review_due ? item.review_due.split('T')[0] : '', changes: item.changes || '' });
    setEditId(item.id); setShowForm(true);
  };

  const del = async (id: string) => { if (window.confirm('Dokument löschen?')) { await qmApi.deleteDocument(id); if (viewDoc?.id === id) setViewDoc(null); load(); } };

  const downloadPdf = async (item: any) => {
    try {
      const res = await qmApi.downloadDocumentPdf(item.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.doc_number}_${item.title.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { alert('PDF konnte nicht erstellt werden.'); }
  };

  const categories: Record<string, string> = { procedure: 'Verfahren', work_instruction: 'Arbeitsanweisung', form: 'Formular', record: 'Aufzeichnung', policy: 'Richtlinie', manual: 'Handbuch' };

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Wird geladen...</p>;

  // Detail view when a document is opened
  if (viewDoc) {
    return (
      <div>
        <button
          style={{ ...btnSecondary, marginBottom: 16 }}
          onClick={() => setViewDoc(null)}
        >
          <ChevronUp size={14} /> Zurück zur Liste
        </button>

        <div style={cardStyle}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'var(--text-muted)', background: 'var(--bg)', padding: '2px 8px', borderRadius: 4 }}>{viewDoc.doc_number}</span>
                <StatusBadge status={viewDoc.status} />
                <span style={badge('#6b7280')}>{categories[viewDoc.category] || viewDoc.category}</span>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Version {viewDoc.version}</span>
              </div>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: 0 }}>{viewDoc.title}</h2>
              {viewDoc.description && <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>{viewDoc.description}</p>}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {viewDoc.doc_number?.startsWith('QM-FB') && (
                <button style={{ ...btnPrimary, background: 'var(--success)' }} onClick={() => onFillForm(viewDoc.doc_number)}>
                  <ClipboardCheck size={14} /> Formular ausfüllen
                </button>
              )}
              <button style={btnPrimary} onClick={() => downloadPdf(viewDoc)}><Download size={14} /> PDF</button>
              <button style={btnSecondary} onClick={() => startEdit(viewDoc)}><Edit2 size={14} /> Bearbeiten</button>
            </div>
          </div>

          {/* Meta info */}
          <div style={{ display: 'flex', gap: 24, padding: '12px 16px', background: 'var(--bg)', borderRadius: 6, marginBottom: 20, fontSize: '0.8rem', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
            {viewDoc.approved_by && <div><strong>Freigabe:</strong> {viewDoc.approved_by}</div>}
            {viewDoc.approved_at && <div><strong>Freigegeben am:</strong> {new Date(viewDoc.approved_at).toLocaleDateString('de-DE')}</div>}
            {viewDoc.review_due && <div style={{ color: new Date(viewDoc.review_due) <= new Date() ? 'var(--danger)' : 'inherit' }}><strong>Nächste Prüfung:</strong> {new Date(viewDoc.review_due).toLocaleDateString('de-DE')}</div>}
            <div><strong>Erstellt:</strong> {new Date(viewDoc.created_at).toLocaleDateString('de-DE')}</div>
            <div><strong>Geändert:</strong> {new Date(viewDoc.updated_at).toLocaleDateString('de-DE')}</div>
          </div>

          {/* Content */}
          {viewDoc.content ? (
            viewDoc.doc_number?.startsWith('QM-FB') ? (
              <FormDocumentRenderer content={viewDoc.content} />
            ) : (
              <div style={{ lineHeight: 1.7, fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
                {viewDoc.content}
              </div>
            )
          ) : (
            <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Kein Inhalt hinterlegt. Klicke auf "Bearbeiten" um Inhalt hinzuzufügen.</p>
          )}

          {/* Changelog */}
          {viewDoc.changes && (
            <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <h4 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8, color: 'var(--text-light)' }}>Änderungshistorie</h4>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>{viewDoc.changes}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={helpBox}>
        <strong>QM-Dokumente verwalten:</strong> Hier pflegen Sie alle wichtigen Unterlagen Ihres Qualitätsmanagementsystems —
        z.B. Verfahrensanweisungen, Arbeitsanweisungen oder Ihr QM-Handbuch.
        Jedes Dokument bekommt eine <em>Dok.-Nr.</em> (z.B. QM-VA-001), eine <em>Version</em> und kann nach Prüfung freigegeben werden.
        Formulare (QM-FB-...) können direkt zum Ausfüllen unter „Formulare" geöffnet werden.
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>{items.length} Dokumente</span>
        <button style={btnPrimary} onClick={() => { resetForm(); setShowForm(true); }}><Plus size={14} /> Neues Dokument</button>
      </div>

      {showForm && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 12 }}>{editId ? 'Dokument bearbeiten' : 'Neues Dokument'}</h3>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Dok.-Nr. *</label><input style={inputStyle} value={form.doc_number} onChange={e => setForm({ ...form, doc_number: e.target.value })} placeholder="QM-VA-001" /></div>
              <div><label style={labelStyle}>Titel *</label><input style={inputStyle} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
              <div><label style={labelStyle}>Version</label><input style={inputStyle} value={form.version} onChange={e => setForm({ ...form, version: e.target.value })} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Kategorie</label>
                <select style={inputStyle} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Status</label>
                <select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="draft">Entwurf</option><option value="review">Prüfung</option>
                  <option value="approved">Freigegeben</option><option value="archived">Archiviert</option>
                </select>
              </div>
              <div><label style={labelStyle}>Freigabe durch</label><input style={inputStyle} value={form.approved_by} onChange={e => setForm({ ...form, approved_by: e.target.value })} /></div>
              <div><label style={labelStyle}>Nächste Prüfung</label><input style={inputStyle} type="date" value={form.review_due} onChange={e => setForm({ ...form, review_due: e.target.value })} /></div>
            </div>
            <div><label style={labelStyle}>Beschreibung</label><textarea style={textareaStyle} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            {form.doc_number?.startsWith('QM-FB') && form.content ? (
              <div>
                <label style={labelStyle}>Formularvorlage</label>
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 20, background: 'var(--bg-card)', maxHeight: 500, overflowY: 'auto' }}>
                  <FormDocumentRenderer content={form.content} />
                </div>
              </div>
            ) : (
              <div><label style={labelStyle}>Inhalt</label><textarea style={{ ...textareaStyle, minHeight: 150 }} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} /></div>
            )}
            <div><label style={labelStyle}>Änderungshistorie</label><textarea style={textareaStyle} value={form.changes} onChange={e => setForm({ ...form, changes: e.target.value })} /></div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={btnPrimary} onClick={save}><Save size={14} /> Speichern</button>
            <button style={btnSecondary} onClick={resetForm}><X size={14} /> Abbrechen</button>
          </div>
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border)' }}>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Nr.</th>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Titel</th>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Kategorie</th>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Version</th>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Status</th>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Prüfung</th>
            <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600 }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr
              key={item.id}
              style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
              onClick={() => setViewDoc(item)}
              onMouseOver={e => (e.currentTarget.style.background = 'var(--bg)')}
              onMouseOut={e => (e.currentTarget.style.background = 'transparent')}
            >
              <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{item.doc_number}</td>
              <td style={{ padding: '8px 12px', fontWeight: 500, color: 'var(--primary-dark)' }}>{item.title}</td>
              <td style={{ padding: '8px 12px' }}>{categories[item.category] || item.category}</td>
              <td style={{ padding: '8px 12px' }}>{item.version}</td>
              <td style={{ padding: '8px 12px' }}><StatusBadge status={item.status} /></td>
              <td style={{ padding: '8px 12px', color: item.review_due && new Date(item.review_due) <= new Date() ? 'var(--danger)' : 'var(--text-muted)' }}>
                {item.review_due ? new Date(item.review_due).toLocaleDateString('de-DE') : '-'}
              </td>
              <td style={{ padding: '8px 12px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                {item.doc_number?.startsWith('QM-FB') && (
                  <button style={{ ...btnSecondary, padding: '4px 8px', background: 'var(--border-light)', color: 'var(--success)', borderColor: 'var(--success)' }} onClick={() => onFillForm(item.doc_number)} title="Formular ausfüllen"><ClipboardCheck size={14} /></button>
                )}
                <button style={{ ...btnSecondary, padding: '4px 8px', marginLeft: 4 }} onClick={() => setViewDoc(item)} title="Öffnen"><Eye size={14} /></button>
                <button style={{ ...btnSecondary, padding: '4px 8px', marginLeft: 4 }} onClick={() => downloadPdf(item)} title="PDF herunterladen"><Download size={14} /></button>
                <button style={{ ...btnSecondary, padding: '4px 8px', marginLeft: 4 }} onClick={() => startEdit(item)} title="Bearbeiten"><Edit2 size={14} /></button>
                <button style={{ ...btnSecondary, padding: '4px 8px', marginLeft: 4 }} onClick={() => del(item.id)} title="Löschen"><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length === 0 && !showForm && <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 32 }}>Noch keine QM-Dokumente angelegt.</p>}
    </div>
  );
}

/* ──────────────── Nonconformities ──────────────── */

function NonconformitiesTab({ initialRisk, onConsumed }: { initialRisk?: any; onConsumed?: () => void }) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ nc_number: '', title: '', description: '', nc_type: 'complaint', source: '', severity: 'minor', status: 'open', root_cause: '', corrective_action: '', preventive_action: '', responsible: '', due_date: '' });

  const load = () => { qmApi.listNCs().then(r => { setItems(r.data.items || r.data); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, []);

  useEffect(() => {
    if (initialRisk) {
      setForm({
        nc_number: '',
        title: `CAPA: ${initialRisk.title}`,
        description: initialRisk.description || '',
        nc_type: 'capa',
        source: `Risiko: ${initialRisk.title}`,
        severity: initialRisk.risk_score >= 15 ? 'critical' : initialRisk.risk_score >= 9 ? 'major' : 'minor',
        status: 'open',
        root_cause: initialRisk.description || '',
        corrective_action: initialRisk.mitigation || '',
        preventive_action: '',
        responsible: initialRisk.responsible || '',
        due_date: '',
      });
      setEditId(null);
      setShowForm(true);
      onConsumed?.();
    }
  }, [initialRisk, onConsumed]);

  const resetForm = () => { setForm({ nc_number: '', title: '', description: '', nc_type: 'complaint', source: '', severity: 'minor', status: 'open', root_cause: '', corrective_action: '', preventive_action: '', responsible: '', due_date: '' }); setEditId(null); setShowForm(false); };

  const save = async () => {
    const payload = { ...form, due_date: form.due_date || null };
    if (editId) { await qmApi.updateNC(editId, payload); } else { await qmApi.createNC(payload); }
    resetForm(); load();
  };

  const startEdit = (item: any) => {
    setForm({ nc_number: item.nc_number, title: item.title, description: item.description || '', nc_type: item.nc_type, source: item.source || '', severity: item.severity, status: item.status, root_cause: item.root_cause || '', corrective_action: item.corrective_action || '', preventive_action: item.preventive_action || '', responsible: item.responsible || '', due_date: item.due_date ? item.due_date.split('T')[0] : '' });
    setEditId(item.id); setShowForm(true);
  };

  const del = async (id: string) => { if (window.confirm('Eintrag löschen?')) { await qmApi.deleteNC(id); load(); } };

  const ncTypes: Record<string, string> = { complaint: 'Beschwerde', nonconformity: 'Abweichung', improvement: 'Verbesserung', capa: 'CAPA' };

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Wird geladen...</p>;

  return (
    <div>
      <div style={helpBox}>
        <strong>CAPA — Korrektur- & Vorbeugungsmaßnahmen:</strong> Hier dokumentieren Sie, wenn etwas nicht nach Plan gelaufen ist und was Sie dagegen tun.
        <em>Korrekturmaßnahme</em> = Was tun wir, damit das Problem nicht nochmal passiert?
        <em>Vorbeugungsmaßnahme</em> = Was tun wir vorsorglich, damit es gar nicht erst entsteht?
        Sie können CAPAs direkt aus einem Risiko heraus erstellen (im Tab „Risiken" per Klick auf das CAPA-Symbol).
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>{items.length} Einträge</span>
        <button style={btnPrimary} onClick={() => { resetForm(); setShowForm(true); }}><Plus size={14} /> Neu</button>
      </div>

      {showForm && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 12 }}>{editId ? 'Bearbeiten' : 'Neu'}</h3>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
              <div><label style={labelStyle}>Nr. *</label><input style={inputStyle} value={form.nc_number} onChange={e => setForm({ ...form, nc_number: e.target.value })} placeholder="NC-2026-001" /></div>
              <div><label style={labelStyle}>Titel *</label><input style={inputStyle} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Typ</label>
                <select style={inputStyle} value={form.nc_type} onChange={e => setForm({ ...form, nc_type: e.target.value })}>
                  {Object.entries(ncTypes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Quelle</label><input style={inputStyle} value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} placeholder="Klient, Audit..." /></div>
              <div><label style={labelStyle}>Schwere</label>
                <select style={inputStyle} value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}>
                  <option value="minor">Gering</option><option value="major">Erheblich</option><option value="critical">Kritisch</option>
                </select>
              </div>
              <div><label style={labelStyle}>Status</label>
                <select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="open">Offen</option><option value="analysis">Analyse</option><option value="corrective_action">Korrektur</option>
                  <option value="verification">Verifizierung</option><option value="closed">Geschlossen</option>
                </select>
              </div>
            </div>
            <div><label style={labelStyle}>Beschreibung</label><textarea style={textareaStyle} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div><label style={labelStyle}>Ursachenanalyse</label><textarea style={textareaStyle} value={form.root_cause} onChange={e => setForm({ ...form, root_cause: e.target.value })} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Korrekturmaßnahme</label><textarea style={textareaStyle} value={form.corrective_action} onChange={e => setForm({ ...form, corrective_action: e.target.value })} /></div>
              <div><label style={labelStyle}>Vorbeugungsmaßnahme</label><textarea style={textareaStyle} value={form.preventive_action} onChange={e => setForm({ ...form, preventive_action: e.target.value })} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Verantwortlich</label><input style={inputStyle} value={form.responsible} onChange={e => setForm({ ...form, responsible: e.target.value })} /></div>
              <div><label style={labelStyle}>Frist</label><input style={inputStyle} type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={btnPrimary} onClick={save}><Save size={14} /> Speichern</button>
            <button style={btnSecondary} onClick={resetForm}><X size={14} /> Abbrechen</button>
          </div>
        </div>
      )}

      {items.map(item => (
        <div key={item.id} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{item.nc_number}</span>
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{item.title}</span>
                <span style={badge('#6b7280')}>{ncTypes[item.nc_type] || item.nc_type}</span>
                <StatusBadge status={item.severity} />
                <StatusBadge status={item.status} />
              </div>
              {item.description && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>{item.description}</div>}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button style={{ ...btnSecondary, padding: '4px 8px' }} onClick={() => startEdit(item)}><Edit2 size={14} /></button>
              <button style={{ ...btnSecondary, padding: '4px 8px' }} onClick={() => del(item.id)}><Trash2 size={14} /></button>
            </div>
          </div>
        </div>
      ))}
      {items.length === 0 && !showForm && <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 32 }}>Keine Beschwerden oder Abweichungen erfasst.</p>}
    </div>
  );
}

/* ──────────────── Audits ──────────────── */

function AuditsTab() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ audit_number: '', title: '', description: '', audit_type: 'internal', scope: '', auditor: '', planned_date: '', conducted_date: '', status: 'planned', findings: '', findings_count: 0, nc_count: 0, recommendations: '', conclusion: '' });

  const load = () => { qmApi.listAudits().then(r => { setItems(r.data.items || r.data); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, []);

  const resetForm = () => { setForm({ audit_number: '', title: '', description: '', audit_type: 'internal', scope: '', auditor: '', planned_date: '', conducted_date: '', status: 'planned', findings: '', findings_count: 0, nc_count: 0, recommendations: '', conclusion: '' }); setEditId(null); setShowForm(false); };

  const save = async () => {
    const payload = { ...form, planned_date: form.planned_date || null, conducted_date: form.conducted_date || null };
    if (editId) { await qmApi.updateAudit(editId, payload); } else { await qmApi.createAudit({ audit_number: form.audit_number, title: form.title, description: form.description || undefined, audit_type: form.audit_type, scope: form.scope || undefined, auditor: form.auditor || undefined, planned_date: form.planned_date || null, status: form.status }); }
    resetForm(); load();
  };

  const startEdit = (item: any) => {
    setForm({ audit_number: item.audit_number, title: item.title, description: item.description || '', audit_type: item.audit_type, scope: item.scope || '', auditor: item.auditor || '', planned_date: item.planned_date ? item.planned_date.split('T')[0] : '', conducted_date: item.conducted_date ? item.conducted_date.split('T')[0] : '', status: item.status, findings: item.findings || '', findings_count: item.findings_count || 0, nc_count: item.nc_count || 0, recommendations: item.recommendations || '', conclusion: item.conclusion || '' });
    setEditId(item.id); setShowForm(true);
  };

  const del = async (id: string) => { if (window.confirm('Audit löschen?')) { await qmApi.deleteAudit(id); load(); } };

  const auditTypes: Record<string, string> = { internal: 'Intern', external: 'Extern', supplier: 'Lieferant' };

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Wird geladen...</p>;

  return (
    <div>
      <div style={helpBox}>
        <strong>Interne Audits planen & durchführen:</strong> Ein Audit ist eine Selbstüberprüfung — Sie schauen sich an, ob Ihre eigenen Abläufe so funktionieren, wie Sie es festgelegt haben.
        Planen Sie mindestens 1x jährlich ein internes Audit. Tragen Sie ein, was geprüft wird (<em>Scope</em>), wer prüft (<em>Auditor</em>)
        und dokumentieren Sie nach der Durchführung die Ergebnisse und Empfehlungen.
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>{items.length} Audits</span>
        <button style={btnPrimary} onClick={() => { resetForm(); setShowForm(true); }}><Plus size={14} /> Neues Audit</button>
      </div>

      {showForm && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 12 }}>{editId ? 'Audit bearbeiten' : 'Neues Audit'}</h3>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Audit-Nr. *</label><input style={inputStyle} value={form.audit_number} onChange={e => setForm({ ...form, audit_number: e.target.value })} placeholder="IA-2026-01" /></div>
              <div><label style={labelStyle}>Titel *</label><input style={inputStyle} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
              <div><label style={labelStyle}>Typ</label>
                <select style={inputStyle} value={form.audit_type} onChange={e => setForm({ ...form, audit_type: e.target.value })}>
                  {Object.entries(auditTypes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Auditor</label><input style={inputStyle} value={form.auditor} onChange={e => setForm({ ...form, auditor: e.target.value })} /></div>
              <div><label style={labelStyle}>Geplant</label><input style={inputStyle} type="date" value={form.planned_date} onChange={e => setForm({ ...form, planned_date: e.target.value })} /></div>
              <div><label style={labelStyle}>Durchgeführt</label><input style={inputStyle} type="date" value={form.conducted_date} onChange={e => setForm({ ...form, conducted_date: e.target.value })} /></div>
              <div><label style={labelStyle}>Status</label>
                <select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="planned">Geplant</option><option value="in_progress">Laufend</option>
                  <option value="completed">Abgeschlossen</option><option value="cancelled">Abgebrochen</option>
                </select>
              </div>
            </div>
            <div><label style={labelStyle}>Umfang / Scope</label><textarea style={textareaStyle} value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} /></div>
            {editId && <>
              <div><label style={labelStyle}>Feststellungen</label><textarea style={textareaStyle} value={form.findings} onChange={e => setForm({ ...form, findings: e.target.value })} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label style={labelStyle}>Anzahl Feststellungen</label><input style={inputStyle} type="number" min={0} value={form.findings_count} onChange={e => setForm({ ...form, findings_count: +e.target.value })} /></div>
                <div><label style={labelStyle}>Davon Abweichungen</label><input style={inputStyle} type="number" min={0} value={form.nc_count} onChange={e => setForm({ ...form, nc_count: +e.target.value })} /></div>
              </div>
              <div><label style={labelStyle}>Empfehlungen</label><textarea style={textareaStyle} value={form.recommendations} onChange={e => setForm({ ...form, recommendations: e.target.value })} /></div>
              <div><label style={labelStyle}>Fazit</label><textarea style={textareaStyle} value={form.conclusion} onChange={e => setForm({ ...form, conclusion: e.target.value })} /></div>
            </>}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={btnPrimary} onClick={save}><Save size={14} /> Speichern</button>
            <button style={btnSecondary} onClick={resetForm}><X size={14} /> Abbrechen</button>
          </div>
        </div>
      )}

      {items.map(item => (
        <div key={item.id} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{item.audit_number}</span>
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{item.title}</span>
                <span style={badge('#6b7280')}>{auditTypes[item.audit_type] || item.audit_type}</span>
                <StatusBadge status={item.status} />
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {item.auditor && <span>Auditor: {item.auditor}</span>}
                {item.planned_date && <span>Geplant: {new Date(item.planned_date).toLocaleDateString('de-DE')}</span>}
                {item.conducted_date && <span>Durchgef.: {new Date(item.conducted_date).toLocaleDateString('de-DE')}</span>}
                {item.findings_count > 0 && <span>Feststellungen: {item.findings_count} ({item.nc_count} NC)</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button style={{ ...btnSecondary, padding: '4px 8px' }} onClick={() => startEdit(item)}><Edit2 size={14} /></button>
              <button style={{ ...btnSecondary, padding: '4px 8px' }} onClick={() => del(item.id)}><Trash2 size={14} /></button>
            </div>
          </div>
        </div>
      ))}
      {items.length === 0 && !showForm && <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 32 }}>Noch keine Audits geplant.</p>}
    </div>
  );
}

/* ──────────────── Management Reviews ──────────────── */

function ReviewsTab() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [form, setForm] = useState({
    review_number: '', title: '', review_date: '', period_from: '', period_to: '', status: 'draft',
    audit_results: '', customer_feedback: '', process_performance: '', nc_summary: '',
    objective_status: '', resource_needs: '', risks_opportunities: '', decisions: '',
    improvement_actions: '', next_review_date: '',
  });

  const load = () => { qmApi.listReviews().then(r => { setItems(r.data.items || r.data); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, []);

  const resetForm = () => {
    setForm({ review_number: '', title: '', review_date: '', period_from: '', period_to: '', status: 'draft', audit_results: '', customer_feedback: '', process_performance: '', nc_summary: '', objective_status: '', resource_needs: '', risks_opportunities: '', decisions: '', improvement_actions: '', next_review_date: '' });
    setEditId(null); setShowForm(false);
  };

  const save = async () => {
    const payload = { ...form, review_date: form.review_date || null, period_from: form.period_from || null, period_to: form.period_to || null, next_review_date: form.next_review_date || null };
    if (editId) { await qmApi.updateReview(editId, payload); } else { await qmApi.createReview({ review_number: form.review_number, title: form.title, review_date: form.review_date || null, period_from: form.period_from || null, period_to: form.period_to || null, status: form.status }); }
    resetForm(); load();
  };

  const startEdit = (item: any) => {
    setForm({
      review_number: item.review_number, title: item.title,
      review_date: item.review_date ? item.review_date.split('T')[0] : '',
      period_from: item.period_from ? item.period_from.split('T')[0] : '',
      period_to: item.period_to ? item.period_to.split('T')[0] : '',
      status: item.status,
      audit_results: item.audit_results || '', customer_feedback: item.customer_feedback || '',
      process_performance: item.process_performance || '', nc_summary: item.nc_summary || '',
      objective_status: item.objective_status || '', resource_needs: item.resource_needs || '',
      risks_opportunities: item.risks_opportunities || '', decisions: item.decisions || '',
      improvement_actions: item.improvement_actions || '',
      next_review_date: item.next_review_date ? item.next_review_date.split('T')[0] : '',
    });
    setEditId(item.id); setShowForm(true);
  };

  const del = async (id: string) => { if (window.confirm('Bewertung löschen?')) { await qmApi.deleteReview(id); load(); } };

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Wird geladen...</p>;

  return (
    <div>
      <div style={helpBox}>
        <strong>Managementbewertung (Management Review):</strong> Einmal jährlich bewerten Sie als Leitung Ihr gesamtes QM-System.
        Dabei schauen Sie auf: Audit-Ergebnisse, Kundenfeedback, Zielerreichung, Abweichungen und Ressourcen.
        Daraus leiten Sie Entscheidungen und Verbesserungen ab. Die ISO 9001 gibt genau vor, welche Punkte bewertet werden müssen —
        die Felder hier folgen Kapitel 9.3 der Norm.
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>{items.length} Managementbewertungen</span>
        <button style={btnPrimary} onClick={() => { resetForm(); setShowForm(true); }}><Plus size={14} /> Neue Bewertung</button>
      </div>

      {showForm && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 12 }}>{editId ? 'Bewertung bearbeiten' : 'Neue Bewertung'}</h3>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Nr. *</label><input style={inputStyle} value={form.review_number} onChange={e => setForm({ ...form, review_number: e.target.value })} placeholder="MB-2026-01" /></div>
              <div><label style={labelStyle}>Titel *</label><input style={inputStyle} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
              <div><label style={labelStyle}>Status</label>
                <select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="draft">Entwurf</option><option value="completed">Abgeschlossen</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div><label style={labelStyle}>Bewertungsdatum</label><input style={inputStyle} type="date" value={form.review_date} onChange={e => setForm({ ...form, review_date: e.target.value })} /></div>
              <div><label style={labelStyle}>Zeitraum von</label><input style={inputStyle} type="date" value={form.period_from} onChange={e => setForm({ ...form, period_from: e.target.value })} /></div>
              <div><label style={labelStyle}>Zeitraum bis</label><input style={inputStyle} type="date" value={form.period_to} onChange={e => setForm({ ...form, period_to: e.target.value })} /></div>
            </div>
            {editId && <>
              <p style={{ fontWeight: 600, fontSize: '0.85rem', marginTop: 8, color: 'var(--text-light)' }}>Eingaben (ISO 9001:2015 Kap. 9.3.2)</p>
              <div><label style={labelStyle}>Auditergebnisse</label><textarea style={textareaStyle} value={form.audit_results} onChange={e => setForm({ ...form, audit_results: e.target.value })} /></div>
              <div><label style={labelStyle}>Kundenfeedback</label><textarea style={textareaStyle} value={form.customer_feedback} onChange={e => setForm({ ...form, customer_feedback: e.target.value })} /></div>
              <div><label style={labelStyle}>Prozessleistung</label><textarea style={textareaStyle} value={form.process_performance} onChange={e => setForm({ ...form, process_performance: e.target.value })} /></div>
              <div><label style={labelStyle}>Zusammenfassung Abweichungen</label><textarea style={textareaStyle} value={form.nc_summary} onChange={e => setForm({ ...form, nc_summary: e.target.value })} /></div>
              <div><label style={labelStyle}>Status Qualitätsziele</label><textarea style={textareaStyle} value={form.objective_status} onChange={e => setForm({ ...form, objective_status: e.target.value })} /></div>
              <div><label style={labelStyle}>Ressourcenbedarf</label><textarea style={textareaStyle} value={form.resource_needs} onChange={e => setForm({ ...form, resource_needs: e.target.value })} /></div>
              <div><label style={labelStyle}>Risiken & Chancen</label><textarea style={textareaStyle} value={form.risks_opportunities} onChange={e => setForm({ ...form, risks_opportunities: e.target.value })} /></div>
              <p style={{ fontWeight: 600, fontSize: '0.85rem', marginTop: 8, color: 'var(--text-light)' }}>Ergebnisse (ISO 9001:2015 Kap. 9.3.3)</p>
              <div><label style={labelStyle}>Entscheidungen</label><textarea style={textareaStyle} value={form.decisions} onChange={e => setForm({ ...form, decisions: e.target.value })} /></div>
              <div><label style={labelStyle}>Verbesserungsmaßnahmen</label><textarea style={textareaStyle} value={form.improvement_actions} onChange={e => setForm({ ...form, improvement_actions: e.target.value })} /></div>
              <div><label style={labelStyle}>Nächste Bewertung</label><input style={inputStyle} type="date" value={form.next_review_date} onChange={e => setForm({ ...form, next_review_date: e.target.value })} /></div>
            </>}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={btnPrimary} onClick={save}><Save size={14} /> Speichern</button>
            <button style={btnSecondary} onClick={resetForm}><X size={14} /> Abbrechen</button>
          </div>
        </div>
      )}

      {items.map(item => (
        <div key={item.id} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ cursor: 'pointer', flex: 1 }} onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{item.review_number}</span>
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{item.title}</span>
                <StatusBadge status={item.status} />
                {expandedId === item.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {item.review_date && <span>Datum: {new Date(item.review_date).toLocaleDateString('de-DE')}</span>}
                {item.period_from && item.period_to && <span>Zeitraum: {new Date(item.period_from).toLocaleDateString('de-DE')} - {new Date(item.period_to).toLocaleDateString('de-DE')}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button style={{ ...btnSecondary, padding: '4px 8px' }} onClick={() => startEdit(item)}><Edit2 size={14} /></button>
              <button style={{ ...btnSecondary, padding: '4px 8px' }} onClick={() => del(item.id)}><Trash2 size={14} /></button>
            </div>
          </div>
          {expandedId === item.id && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'grid', gap: 8, fontSize: '0.85rem' }}>
              {item.audit_results && <div><strong>Auditergebnisse:</strong> {item.audit_results}</div>}
              {item.customer_feedback && <div><strong>Kundenfeedback:</strong> {item.customer_feedback}</div>}
              {item.process_performance && <div><strong>Prozessleistung:</strong> {item.process_performance}</div>}
              {item.nc_summary && <div><strong>Abweichungen:</strong> {item.nc_summary}</div>}
              {item.decisions && <div><strong>Entscheidungen:</strong> {item.decisions}</div>}
              {item.improvement_actions && <div><strong>Maßnahmen:</strong> {item.improvement_actions}</div>}
            </div>
          )}
        </div>
      ))}
      {items.length === 0 && !showForm && <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 32 }}>Noch keine Managementbewertungen durchgeführt.</p>}
    </div>
  );
}


/* ──────────────── Form Document Renderer ──────────────── */

function FormDocumentRenderer({ content }: { content: string }) {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let idx = 0;

  // --- Styles ---
  const sectionHeader: React.CSSProperties = {
    background: 'var(--primary)', color: 'white', padding: '10px 16px',
    borderRadius: 6, fontWeight: 700, fontSize: '0.95rem', marginTop: 24, marginBottom: 16,
    letterSpacing: '0.02em',
  };
  const subHeader: React.CSSProperties = {
    fontWeight: 700, fontSize: '0.9rem', color: 'var(--primary)',
    borderBottom: '2px solid var(--primary)', paddingBottom: 6,
    marginTop: 20, marginBottom: 12,
  };
  const fieldGrid: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12,
  };
  const lbl: React.CSSProperties = {
    fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', marginBottom: 4, display: 'block',
  };
  const inp: React.CSSProperties = {
    width: '100%', padding: '8px 12px', border: '1px solid var(--border)',
    borderRadius: 6, background: 'var(--bg)', fontSize: '0.85rem', color: 'var(--text-muted)', minHeight: 36,
  };
  const txa: React.CSSProperties = { ...inp, minHeight: 72 };
  const cbRow: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 8 };
  const cbItem: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' };
  const cbBox: React.CSSProperties = {
    width: 18, height: 18, border: '2px solid var(--border)', borderRadius: 4,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--bg-card)', flexShrink: 0,
  };
  const ratingRow: React.CSSProperties = { display: 'flex', gap: 4, marginBottom: 8 };
  const ratingBtn: React.CSSProperties = {
    width: 36, height: 36, border: '2px solid var(--border)', borderRadius: 6,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.85rem', fontWeight: 600, background: 'var(--bg-card)', color: 'var(--text-light)',
  };
  const infoBox: React.CSSProperties = {
    background: 'var(--border-light)', border: '1px solid var(--border)', borderRadius: 8,
    padding: '12px 16px', fontSize: '0.85rem', color: 'var(--info)', lineHeight: 1.6, marginBottom: 16,
  };

  // --- Helpers ---
  const hasCB = (s: string) => /\[\s*\]/.test(s);
  const hasBlank = (s: string) => /_{4,}/.test(s);
  const isScale = (s: string) => /\[\s*\]\s*1\s+\[\s*\]\s*2/.test(s);
  const isEval = (s: string) => /\[\s*\]\s*E\s+\[\s*\]\s*T/.test(s);
  const parseCBOpts = (s: string): string[] => {
    const m = s.match(/\[\s*\]\s*([^[\]]+?)(?=\s*\[\s*\]|$)/g);
    return m ? m.map(x => x.replace(/\[\s*\]\s*/, '').trim()).filter(Boolean) : [];
  };
  const nextNonEmpty = (from: number): number => {
    let j = from;
    while (j < lines.length && !lines[j].trim()) j++;
    return j;
  };
  const isSection = (t: string) => /^TEIL\s+[A-Z0-9]/.test(t);
  const isNumberedQ = (t: string) => /^\d+[.)]\s/.test(t);
  const isSubNumberedQ = (t: string) => /^\d+\.\d+\s/.test(t);
  const isInfoStart = (t: string) => /^(ANLEITUNG|HINWEISE|Dieses Formular|Auszufüllen|Alle Angaben|Für jede Frage)/.test(t);
  const isSubSec = (t: string) => {
    if (t.length < 4 || t.length > 80) return false;
    // "1. KONTEXT DER ORGANISATION (Kap. 4)" — numbered + all-caps
    if (/^\d+\.\s+[A-Z\u00C4\u00D6\u00DC]/.test(t) && !/[a-z\u00E4\u00F6\u00FC\u00DF]/.test(t)) return true;
    // Pure all-caps short labels like COACHING-PROZESS, COACH, ORGANISATION, ZUSAMMENFASSUNG
    if (/^[A-Z\u00C4\u00D6\u00DC\s-]+$/.test(t) && t.length >= 4 && t.length <= 40 &&
        !isInfoStart(t)) return true;
    return false;
  };

  // --- Render helpers ---
  const renderCheckboxes = (label: string, cbLines: string[], key: string) => {
    const allOpts: string[] = [];
    let scale = false;
    let evalType = false;
    for (const cl of cbLines) {
      if (isScale(cl)) { scale = true; break; }
      if (isEval(cl)) { evalType = true; allOpts.push(...parseCBOpts(cl)); continue; }
      allOpts.push(...parseCBOpts(cl));
    }
    if (scale) {
      // Check if it's 1-6 scale (Schulnote)
      const hasExtra = cbLines.some(l => /\[\s*\]\s*6/.test(l));
      const nums = hasExtra ? [1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5];
      return (
        <div key={key} style={{ marginBottom: 16 }}>
          {label && <span style={lbl}>{label}</span>}
          <div style={ratingRow}>
            {nums.map(n => <div key={n} style={ratingBtn}>{n}</div>)}
          </div>
        </div>
      );
    }
    if (evalType) {
      return (
        <div key={key} style={{ marginBottom: 12 }}>
          {label && <span style={lbl}>{label}</span>}
          <div style={cbRow}>
            {['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'].map((o, j) => (
              <label key={j} style={cbItem}><span style={cbBox} /> {o}</label>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div key={key} style={{ marginBottom: 16 }}>
        {label && <span style={lbl}>{label}</span>}
        <div style={cbRow}>
          {allOpts.map((o, j) => <label key={j} style={cbItem}><span style={cbBox} /> {o}</label>)}
        </div>
      </div>
    );
  };

  const renderBlankField = (label: string, key: string, long?: boolean) => (
    <div key={key} style={{ marginBottom: 12 }}>
      <span style={lbl}>{label}</span>
      <div style={long ? txa : inp} />
    </div>
  );

  const isLongField = (label: string) =>
    /Beschreibung|Ursache|Maßnahme|Maßnahme|Lösung|Loesung|Wirksamkeit|Feststellung|Empfohl|Fazit|Was |Welche |besonders|verbessert|Anmerkungen|Wünsche|Wuensche/i.test(label);

  // ====== PASS 1: Title block (first non-empty lines before sections/questions/blanks) ======
  const titleLines: string[] = [];
  while (idx < lines.length) {
    const t = lines[idx].trim();
    if (!t) { idx++; continue; }
    if (isSection(t) || isInfoStart(t) || hasCB(t) || hasBlank(t) || isNumberedQ(t) || isSubNumberedQ(t)) break;
    titleLines.push(t);
    idx++;
  }
  if (titleLines.length > 0) {
    elements.push(
      <div key="title" style={{ textAlign: 'center', marginBottom: 20, paddingBottom: 16, borderBottom: '3px double var(--border)' }}>
        <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--primary)', margin: 0 }}>{titleLines[0]}</h2>
        {titleLines.slice(1).map((sl, j) => (
          <div key={j} style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 4 }}>{sl}</div>
        ))}
      </div>
    );
  }

  // ====== PASS 2: Main content ======
  while (idx < lines.length) {
    const t = lines[idx].trim();
    if (!t) { idx++; continue; }

    // --- Info/instruction block ---
    if (isInfoStart(t)) {
      const infoLines = [t]; idx++;
      while (idx < lines.length) {
        const nt = lines[idx].trim();
        if (!nt || isSection(nt) || isSubSec(nt) || hasCB(nt) || hasBlank(nt) || isNumberedQ(nt) || isSubNumberedQ(nt)) break;
        if (nt.startsWith('- ') || nt.startsWith('* ')) break; // bullet list = separate
        infoLines.push(nt); idx++;
      }
      elements.push(<div key={`info-${idx}`} style={infoBox}>{infoLines.join(' ')}</div>);
      continue;
    }

    // --- Bullet list (HINWEISE section items) ---
    if (t.startsWith('- ') || t.startsWith('* ')) {
      const bullets: string[] = [];
      while (idx < lines.length && (lines[idx].trim().startsWith('- ') || lines[idx].trim().startsWith('* '))) {
        bullets.push(lines[idx].trim().replace(/^[-*]\s*/, ''));
        idx++;
      }
      elements.push(
        <ul key={`ul-${idx}`} style={{ margin: '8px 0 16px 8px', padding: 0, listStyle: 'none', fontSize: '0.85rem' }}>
          {bullets.map((b, j) => (
            <li key={j} style={{ padding: '4px 0 4px 16px', borderLeft: '3px solid var(--primary)', marginBottom: 4, color: 'var(--text)' }}>{b}</li>
          ))}
        </ul>
      );
      continue;
    }

    // --- Section header (TEIL A, TEIL B etc.) ---
    if (isSection(t)) {
      elements.push(<div key={`sec-${idx}`} style={sectionHeader}>{t}</div>);
      idx++; continue;
    }

    // --- Subsection header (all-caps like COACHING-PROZESS, numbered all-caps like "1. KONTEXT...") ---
    if (isSubSec(t) && !hasCB(t) && !hasBlank(t)) {
      elements.push(<div key={`sub-${idx}`} style={subHeader}>{t}</div>);
      idx++; continue;
    }

    // --- Signature / bottom fields ---
    if (/^(Unterschrift|Datum:)/.test(t) && hasBlank(t)) {
      const parts = t.split(/\s{2,}/).filter(Boolean);
      elements.push(
        <div key={`sig-${idx}`} style={{ ...fieldGrid, marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          {parts.map((p, j) => (
            <div key={j}>
              <span style={lbl}>{p.replace(/_+/g, '').replace(/:\s*$/, '').trim()}</span>
              <div style={inp} />
            </div>
          ))}
        </div>
      );
      idx++; continue;
    }

    // --- Line with blanks (field: ________) but NO checkboxes ---
    if (hasBlank(t) && !hasCB(t)) {
      // Check if multiple fields on one line (separated by 3+ spaces)
      const parts = t.split(/\s{3,}/).filter(Boolean);
      if (parts.length > 1 && parts.every(p => hasBlank(p))) {
        elements.push(
          <div key={`fg-${idx}`} style={fieldGrid}>
            {parts.map((p, j) => {
              const label = p.replace(/_+/g, '').replace(/:\s*$/, '').trim();
              return (
                <div key={j}>
                  <span style={lbl}>{label}</span>
                  <div style={inp} />
                </div>
              );
            })}
          </div>
        );
      } else {
        // Single blank field — could be standalone (like "________") or labeled ("Betreff: ________")
        const label = t.replace(/_+/g, '').replace(/:\s*$/, '').trim();
        if (label) {
          elements.push(renderBlankField(label, `bf-${idx}`, isLongField(label)));
        }
        // Skip consecutive blank-only lines (extra space for textarea already handled)
        while (idx + 1 < lines.length && /^_+$/.test(lines[idx + 1].trim())) idx++;
      }
      idx++; continue;
    }

    // --- Numbered question (e.g. "4. Die Ziele..." or "20. Gesamtnote...") ---
    // --- Sub-numbered question (e.g. "1.1 Sind die..." or "4.2 Kompetenzen...") ---
    if (isNumberedQ(t) || isSubNumberedQ(t)) {
      const qLabel = t.replace(/^\d+[.)]\s*/, '').replace(/^\d+\.\d+\s*/, '').trim();
      const ni = nextNonEmpty(idx + 1);

      // Next line is checkbox?
      if (ni < lines.length && hasCB(lines[ni].trim())) {
        // Collect all consecutive checkbox lines
        const cbLines: string[] = [];
        let ci = ni;
        while (ci < lines.length && hasCB(lines[ci].trim())) { cbLines.push(lines[ci].trim()); ci++; }
        elements.push(renderCheckboxes(qLabel, cbLines, `q-${idx}`));
        // Check for Feststellung field after eval checkboxes
        const fi = nextNonEmpty(ci);
        if (fi < lines.length && /^Feststellung/i.test(lines[fi].trim())) {
          elements.push(
            <div key={`fest-${idx}`} style={{ marginBottom: 16, marginLeft: 16 }}>
              <span style={{ ...lbl, fontSize: '0.8rem', color: 'var(--text-muted)' }}>Feststellung</span>
              <div style={inp} />
            </div>
          );
          idx = fi + 1;
        } else {
          idx = ci;
        }
        continue;
      }

      // Next line is blank?
      if (ni < lines.length && hasBlank(lines[ni].trim())) {
        // Skip consecutive blank lines
        let bi = ni;
        while (bi + 1 < lines.length && /^_+$/.test(lines[bi + 1].trim())) bi++;
        elements.push(renderBlankField(qLabel, `q-${idx}`, isLongField(qLabel) || bi > ni));
        idx = bi + 1; continue;
      }

      // Question with no following options — just an input field
      elements.push(renderBlankField(qLabel, `q-${idx}`, false));
      idx++; continue;
    }

    // --- Text line ending with ":" — look ahead for checkboxes or blanks ---
    if (t.endsWith(':') || t.endsWith('?')) {
      const label = t.replace(/[:\s]+$/, '').trim();
      const ni = nextNonEmpty(idx + 1);

      // Followed by checkboxes?
      if (ni < lines.length && hasCB(lines[ni].trim())) {
        const cbLines: string[] = [];
        let ci = ni;
        while (ci < lines.length && hasCB(lines[ci].trim())) { cbLines.push(lines[ci].trim()); ci++; }
        elements.push(renderCheckboxes(label, cbLines, `tq-${idx}`));
        idx = ci; continue;
      }

      // Followed by blank?
      if (ni < lines.length && hasBlank(lines[ni].trim())) {
        let bi = ni;
        while (bi + 1 < lines.length && /^_+$/.test(lines[bi + 1].trim())) bi++;
        elements.push(renderBlankField(label, `tq-${idx}`, isLongField(label) || bi > ni));
        idx = bi + 1; continue;
      }

      // Standalone label — render as label + input
      elements.push(renderBlankField(label, `tq-${idx}`, isLongField(label)));
      idx++; continue;
    }

    // --- Standalone checkbox line (no label text before it) ---
    if (hasCB(t)) {
      const cbLines: string[] = [];
      while (idx < lines.length && hasCB(lines[idx].trim())) {
        cbLines.push(lines[idx].trim()); idx++;
      }
      // Try to extract inline label (text before first [ ])
      const firstPart = cbLines[0].split(/\[\s*\]/)[0].trim();
      const inlineLabel = firstPart && !firstPart.match(/^\d/) ? firstPart.replace(/:$/, '') : '';
      elements.push(renderCheckboxes(inlineLabel, cbLines, `cb-${idx}`));
      continue;
    }

    // --- "Anzahl..." summary lines with short blanks (____) ---
    if (/:\s+_+/.test(t)) {
      const label = t.replace(/_+/g, '').replace(/:\s*$/, '').trim();
      elements.push(renderBlankField(label, `sf-${idx}`, false));
      idx++; continue;
    }

    // --- Plain text / thank-you note ---
    elements.push(
      <p key={`p-${idx}`} style={{ fontSize: '0.85rem', color: 'var(--text)', lineHeight: 1.6, margin: '8px 0' }}>
        {t}
      </p>
    );
    idx++;
  }

  return <div>{elements}</div>;
}

/* ──────────────── Ablage (QM Records) ──────────────── */

interface FormFieldDef {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'date' | 'select' | 'rating';
  options?: string[];
  required?: boolean;
}

interface FormSection {
  title: string;
  fields: FormFieldDef[];
}

interface FormSchema {
  title: string;
  description: string;
  sections: FormSection[];
}

const FORM_SCHEMAS: Record<string, FormSchema> = {
  feedback: {
    title: 'Feedbackbogen',
    description: 'Klientenfeedback nach Coaching-Sitzung (QM-FB-001)',
    sections: [
      {
        title: 'Allgemeine Angaben',
        fields: [
          { key: 'client_name', label: 'Klient/Coachee', type: 'text', required: true },
          { key: 'session_date', label: 'Datum der Sitzung', type: 'date', required: true },
          { key: 'coach_name', label: 'Coach', type: 'text' },
          { key: 'session_type', label: 'Art der Sitzung', type: 'select', options: ['Einzelcoaching', 'Teamcoaching', 'Workshop', 'Sparring', 'Mediation'] },
        ],
      },
      {
        title: 'Bewertung (1 = sehr schlecht, 5 = sehr gut)',
        fields: [
          { key: 'satisfaction', label: 'Gesamtzufriedenheit', type: 'rating' },
          { key: 'goal_focus', label: 'Wurde auf Ihre Anliegen eingegangen?', type: 'rating' },
          { key: 'methods', label: 'War die Methodik hilfreich?', type: 'rating' },
          { key: 'development', label: 'Fühlen Sie sich in Ihrer Entwicklung unterstützt?', type: 'rating' },
          { key: 'atmosphere', label: 'War die Atmosphäre vertrauensvoll?', type: 'rating' },
        ],
      },
      {
        title: 'Offene Fragen',
        fields: [
          { key: 'positive', label: 'Was hat Ihnen besonders gut gefallen?', type: 'textarea' },
          { key: 'improvement', label: 'Was könnte verbessert werden?', type: 'textarea' },
          { key: 'recommendation', label: 'Würden Sie das Coaching weiterempfehlen?', type: 'select', options: ['Ja', 'Nein', 'Vielleicht'] },
          { key: 'notes', label: 'Weitere Anmerkungen', type: 'textarea' },
        ],
      },
    ],
  },
  audit: {
    title: 'Audit-Checkliste',
    description: 'Interne Audit-Checkliste nach ISO 9001:2015 (QM-FB-002)',
    sections: [
      {
        title: 'Audit-Informationen',
        fields: [
          { key: 'audit_number', label: 'Audit-Nr.', type: 'text', required: true },
          { key: 'audit_date', label: 'Datum', type: 'date', required: true },
          { key: 'auditor', label: 'Auditor', type: 'text', required: true },
          { key: 'area', label: 'Auditierter Bereich', type: 'text' },
          { key: 'scope', label: 'Auditumfang', type: 'text' },
        ],
      },
      {
        title: 'Kap. 4 — Kontext der Organisation',
        fields: [
          { key: 'ctx_stakeholders', label: 'Sind interessierte Parteien identifiziert?', type: 'select', options: ['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'] },
          { key: 'ctx_stakeholders_note', label: 'Feststellung', type: 'text' },
          { key: 'ctx_scope', label: 'Ist der Anwendungsbereich definiert?', type: 'select', options: ['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'] },
          { key: 'ctx_scope_note', label: 'Feststellung', type: 'text' },
        ],
      },
      {
        title: 'Kap. 5 — Führung',
        fields: [
          { key: 'lead_policy', label: 'Ist die Qualitätspolitik kommuniziert?', type: 'select', options: ['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'] },
          { key: 'lead_policy_note', label: 'Feststellung', type: 'text' },
          { key: 'lead_roles', label: 'Sind Verantwortlichkeiten definiert?', type: 'select', options: ['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'] },
          { key: 'lead_roles_note', label: 'Feststellung', type: 'text' },
        ],
      },
      {
        title: 'Kap. 6 — Planung',
        fields: [
          { key: 'plan_risks', label: 'Sind Risiken und Chancen bewertet?', type: 'select', options: ['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'] },
          { key: 'plan_risks_note', label: 'Feststellung', type: 'text' },
          { key: 'plan_objectives', label: 'Sind Qualitätsziele messbar definiert?', type: 'select', options: ['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'] },
          { key: 'plan_objectives_note', label: 'Feststellung', type: 'text' },
        ],
      },
      {
        title: 'Kap. 7 — Unterstützung',
        fields: [
          { key: 'sup_competence', label: 'Sind Kompetenzen nachgewiesen?', type: 'select', options: ['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'] },
          { key: 'sup_competence_note', label: 'Feststellung', type: 'text' },
          { key: 'sup_documents', label: 'Ist die Dokumentenlenkung umgesetzt?', type: 'select', options: ['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'] },
          { key: 'sup_documents_note', label: 'Feststellung', type: 'text' },
        ],
      },
      {
        title: 'Kap. 8 — Betrieb',
        fields: [
          { key: 'op_process', label: 'Sind Prozesse geplant und gesteuert?', type: 'select', options: ['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'] },
          { key: 'op_process_note', label: 'Feststellung', type: 'text' },
          { key: 'op_service', label: 'Ist die Dienstleistungserbringung konform?', type: 'select', options: ['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'] },
          { key: 'op_service_note', label: 'Feststellung', type: 'text' },
        ],
      },
      {
        title: 'Kap. 9 — Leistungsbewertung',
        fields: [
          { key: 'perf_monitoring', label: 'Ist Überwachung/Messung vorhanden?', type: 'select', options: ['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'] },
          { key: 'perf_monitoring_note', label: 'Feststellung', type: 'text' },
          { key: 'perf_audit', label: 'Sind interne Audits geplant?', type: 'select', options: ['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'] },
          { key: 'perf_audit_note', label: 'Feststellung', type: 'text' },
        ],
      },
      {
        title: 'Kap. 10 — Verbesserung',
        fields: [
          { key: 'imp_nc', label: 'Werden Nichtkonformitäten behandelt?', type: 'select', options: ['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'] },
          { key: 'imp_nc_note', label: 'Feststellung', type: 'text' },
          { key: 'imp_kpv', label: 'Findet fortlaufende Verbesserung statt?', type: 'select', options: ['Erfüllt', 'Teilweise', 'Nicht erfüllt', 'N/A'] },
          { key: 'imp_kpv_note', label: 'Feststellung', type: 'text' },
        ],
      },
      {
        title: 'Zusammenfassung',
        fields: [
          { key: 'findings_positive', label: 'Positive Feststellungen', type: 'textarea' },
          { key: 'findings_improvement', label: 'Verbesserungspotenziale', type: 'textarea' },
          { key: 'nc_count', label: 'Anzahl Abweichungen', type: 'text' },
          { key: 'conclusion', label: 'Gesamtbewertung', type: 'select', options: ['Bestanden', 'Bestanden mit Auflagen', 'Nicht bestanden'] },
          { key: 'recommendations', label: 'Empfehlungen', type: 'textarea' },
          { key: 'next_audit_date', label: 'Nächster Audit-Termin', type: 'date' },
        ],
      },
    ],
  },
  complaint: {
    title: 'Beschwerdeformular',
    description: 'Beschwerdemanagement nach ISO 9001:2015 (QM-FB-003)',
    sections: [
      {
        title: 'Beschwerdeeingang',
        fields: [
          { key: 'complaint_number', label: 'Beschwerde-Nr.', type: 'text', required: true },
          { key: 'received_date', label: 'Eingangsdatum', type: 'date', required: true },
          { key: 'received_by', label: 'Entgegengenommen von', type: 'text' },
          { key: 'source', label: 'Quelle', type: 'select', options: ['Klient', 'Auftraggeber', 'Partner', 'Intern', 'Sonstige'] },
          { key: 'complainant_name', label: 'Beschwerdeführer (Name)', type: 'text' },
          { key: 'complainant_contact', label: 'Kontakt (E-Mail/Telefon)', type: 'text' },
        ],
      },
      {
        title: 'Beschwerdebeschreibung',
        fields: [
          { key: 'subject', label: 'Betreff', type: 'text', required: true },
          { key: 'description', label: 'Beschreibung der Beschwerde', type: 'textarea', required: true },
          { key: 'affected_area', label: 'Betroffener Bereich', type: 'select', options: ['Coaching-Prozess', 'Kommunikation', 'Terminplanung', 'Abrechnung', 'Vertraulichkeit', 'Methodik', 'Sonstiges'] },
          { key: 'severity', label: 'Schweregrad', type: 'select', options: ['Gering', 'Mittel', 'Hoch', 'Kritisch'] },
        ],
      },
      {
        title: 'Bearbeitung',
        fields: [
          { key: 'immediate_action', label: 'Sofortmaßnahme', type: 'textarea' },
          { key: 'root_cause', label: 'Ursachenanalyse (5-Why / Ishikawa)', type: 'textarea' },
          { key: 'corrective_action', label: 'Korrekturmaßnahme', type: 'textarea' },
          { key: 'preventive_action', label: 'Vorbeugungsmaßnahme', type: 'textarea' },
          { key: 'responsible', label: 'Verantwortlich', type: 'text' },
          { key: 'due_date', label: 'Frist', type: 'date' },
        ],
      },
      {
        title: 'Abschluss',
        fields: [
          { key: 'resolution', label: 'Lösung / Ergebnis', type: 'textarea' },
          { key: 'client_satisfied', label: 'Klient zufrieden?', type: 'select', options: ['Ja', 'Nein', 'Teilweise', 'Ausstehend'] },
          { key: 'effectiveness_check', label: 'Wirksamkeitsprüfung', type: 'textarea' },
          { key: 'closed_date', label: 'Abschlussdatum', type: 'date' },
          { key: 'complaint_status', label: 'Status', type: 'select', options: ['Offen', 'In Bearbeitung', 'Abgeschlossen'] },
        ],
      },
    ],
  },
};

const templateTypeLabels: Record<string, string> = {
  feedback: 'Feedbackbogen', audit: 'Audit-Checkliste', complaint: 'Beschwerde',
};

const templateTypeColors: Record<string, string> = {
  feedback: '#10b981', audit: '#3b82f6', complaint: '#ef4444',
};

function RatingInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          style={{
            width: 36, height: 36, borderRadius: 6, border: '1px solid var(--border)',
            background: n <= value ? 'var(--primary)' : 'var(--bg)',
            color: n <= value ? 'white' : 'var(--text-muted)',
            cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

function AblageTab({ initialFormType, onConsumed }: { initialFormType?: string | null; onConsumed?: () => void }) {
  const { showToast } = useToast();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'list' | 'choose' | 'form' | 'detail'>('list');
  const [filterType, setFilterType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [editId, setEditId] = useState<string | null>(null);
  const [fieldData, setFieldData] = useState<Record<string, any>>({});
  const [recordNumber, setRecordNumber] = useState('');
  const [recordTitle, setRecordTitle] = useState('');
  const [recordStatus, setRecordStatus] = useState('draft');
  const [viewRecord, setViewRecord] = useState<any>(null);

  const load = () => {
    const params: any = {};
    if (filterType) params.template_type = filterType;
    if (filterStatus) params.status = filterStatus;
    qmApi.listRecords(params).then(r => { setItems(r.data.items || r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, [filterType, filterStatus]);

  // Auto-start form when navigated from Dokumente tab
  useEffect(() => {
    if (initialFormType && FORM_SCHEMAS[initialFormType]) {
      startNew(initialFormType);
      onConsumed?.();
    }
  }, [initialFormType]); // eslint-disable-line react-hooks/exhaustive-deps

  const startNew = async (type: string) => {
    setSelectedType(type);
    setFieldData({});
    setEditId(null);
    setRecordStatus('draft');
    setRecordTitle('');
    try {
      const res = await qmApi.nextRecordNumber(type);
      setRecordNumber(res.data.number);
    } catch { setRecordNumber(''); }
    setView('form');
  };

  const startEdit = (item: any) => {
    setSelectedType(item.template_type);
    setFieldData(item.field_data || {});
    setEditId(item.id);
    setRecordNumber(item.record_number);
    setRecordTitle(item.title);
    setRecordStatus(item.status);
    setView('form');
  };

  const openDetail = (item: any) => {
    setViewRecord(item);
    setView('detail');
  };

  const save = async (status?: string) => {
    const finalStatus = status || recordStatus;
    const schema = FORM_SCHEMAS[selectedType];
    const title = recordTitle || `${schema?.title || selectedType} ${recordNumber}`;
    const payload = {
      record_number: recordNumber,
      title,
      template_type: selectedType,
      field_data: fieldData,
      status: finalStatus,
    };
    try {
      if (editId) {
        await qmApi.updateRecord(editId, { title, field_data: fieldData, status: finalStatus });
      } else {
        await qmApi.createRecord(payload);
      }
      setView('list'); load();
    } catch { alert('Speichern fehlgeschlagen.'); }
  };

  const del = async (id: string) => {
    if (window.confirm('Eintrag löschen?')) {
      await qmApi.deleteRecord(id);
      if (viewRecord?.id === id) setView('list');
      load();
    }
  };

  const downloadPdf = async (item: any) => {
    try {
      const res = await qmApi.downloadRecordPdf(item.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.record_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { alert('PDF konnte nicht erstellt werden.'); }
  };

  const setField = (key: string, value: any) => {
    setFieldData(prev => ({ ...prev, [key]: value }));
  };

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Wird geladen...</p>;

  /* ---- Detail view ---- */
  if (view === 'detail' && viewRecord) {
    const schema = FORM_SCHEMAS[viewRecord.template_type];
    const fd = viewRecord.field_data || {};
    return (
      <div>
        <button style={{ ...btnSecondary, marginBottom: 16 }} onClick={() => { setView('list'); setViewRecord(null); }}>
          <ChevronUp size={14} /> Zurück zur Liste
        </button>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'var(--text-muted)', background: 'var(--bg)', padding: '2px 8px', borderRadius: 4 }}>{viewRecord.record_number}</span>
                <StatusBadge status={viewRecord.status} />
                <span style={badge(templateTypeColors[viewRecord.template_type] || '#6b7280')}>
                  {templateTypeLabels[viewRecord.template_type] || viewRecord.template_type}
                </span>
              </div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>{viewRecord.title}</h2>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button style={btnPrimary} onClick={() => downloadPdf(viewRecord)}><Download size={14} /> PDF</button>
              <button style={btnSecondary} onClick={() => startEdit(viewRecord)}><Edit2 size={14} /> Bearbeiten</button>
            </div>
          </div>

          {schema?.sections.map((section, si) => (
            <div key={si} style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--primary)', padding: '8px 12px', background: 'var(--bg)', borderRadius: 6, marginBottom: 12 }}>{section.title}</h3>
              <div style={{ display: 'grid', gap: 10, paddingLeft: 12 }}>
                {section.fields.map(f => (
                  <div key={f.key} style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 8, fontSize: '0.85rem', alignItems: 'start' }}>
                    <span style={{ fontWeight: 500, color: 'var(--text-light)' }}>{f.label}:</span>
                    <span style={{ color: fd[f.key] ? 'var(--text)' : 'var(--text-muted)' }}>
                      {f.type === 'rating' && fd[f.key] ? (
                        <span style={{ display: 'flex', gap: 2 }}>
                          {[1,2,3,4,5].map(n => (
                            <Star key={n} size={16} fill={n <= fd[f.key] ? 'var(--primary)' : 'none'} stroke={n <= fd[f.key] ? 'var(--primary)' : 'var(--text-muted)'} />
                          ))}
                        </span>
                      ) : (fd[f.key] || '-')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            Erstellt: {new Date(viewRecord.created_at).toLocaleDateString('de-DE')}
            {viewRecord.filed_at && <> | Abgeschlossen: {new Date(viewRecord.filed_at).toLocaleDateString('de-DE')}</>}
          </div>
        </div>
      </div>
    );
  }

  /* ---- Choose form type ---- */
  if (view === 'choose') {
    return (
      <div>
        <button style={{ ...btnSecondary, marginBottom: 16 }} onClick={() => setView('list')}>
          <ChevronUp size={14} /> Zurück
        </button>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 16 }}>Welches Formular möchten Sie ausfüllen?</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {Object.entries(FORM_SCHEMAS).map(([key, schema]) => (
            <div
              key={key}
              onClick={() => startNew(key)}
              style={{
                ...cardStyle, cursor: 'pointer', textAlign: 'center',
                borderLeft: `4px solid ${templateTypeColors[key]}`,
                transition: 'box-shadow 0.15s',
              }}
              onMouseOver={e => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)')}
              onMouseOut={e => (e.currentTarget.style.boxShadow = 'none')}
            >
              <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>
                {key === 'feedback' ? '📋' : key === 'audit' ? '🔍' : '⚠️'}
              </div>
              <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 4 }}>{schema.title}</h4>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>{schema.description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ---- Form view ---- */
  if (view === 'form' && selectedType) {
    const schema = FORM_SCHEMAS[selectedType];
    return (
      <div>
        <button style={{ ...btnSecondary, marginBottom: 16 }} onClick={() => setView('list')}>
          <ChevronUp size={14} /> Zurück zur Liste
        </button>

        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
                {editId ? 'Formular bearbeiten' : 'Neues Formular'}: {schema.title}
              </h2>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>{schema.description}</p>
            </div>
            <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'var(--text-muted)', background: 'var(--bg)', padding: '4px 10px', borderRadius: 4 }}>{recordNumber}</span>
          </div>

          {/* Title field */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Titel des Eintrags</label>
            <input
              style={inputStyle}
              value={recordTitle}
              onChange={e => setRecordTitle(e.target.value)}
              placeholder={`z.B. ${schema.title} — Klient XY, ${new Date().toLocaleDateString('de-DE')}`}
            />
          </div>

          {/* Sections with individual fields */}
          {schema.sections.map((section, si) => (
            <div key={si} style={{ marginBottom: 24 }}>
              <h3 style={{
                fontSize: '0.9rem', fontWeight: 600, color: 'var(--primary)',
                padding: '8px 12px', background: 'var(--bg)', borderRadius: 6, marginBottom: 16,
                borderLeft: `3px solid ${templateTypeColors[selectedType]}`,
              }}>
                {section.title}
              </h3>
              <div style={{ display: 'grid', gap: 14, paddingLeft: 4 }}>
                {section.fields.map(f => (
                  <div key={f.key} style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 12, alignItems: 'start' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-light)', paddingTop: 8 }}>
                      {f.label} {f.required && <span style={{ color: 'var(--danger)' }}>*</span>}
                    </label>
                    <div>
                      {f.type === 'text' && (
                        <input style={inputStyle} value={fieldData[f.key] || ''} onChange={e => setField(f.key, e.target.value)} />
                      )}
                      {f.type === 'date' && (
                        <input style={inputStyle} type="date" value={fieldData[f.key] || ''} onChange={e => setField(f.key, e.target.value)} />
                      )}
                      {f.type === 'textarea' && (
                        <textarea style={{ ...textareaStyle, minHeight: 70 }} value={fieldData[f.key] || ''} onChange={e => setField(f.key, e.target.value)} />
                      )}
                      {f.type === 'select' && (
                        <select style={inputStyle} value={fieldData[f.key] || ''} onChange={e => setField(f.key, e.target.value)}>
                          <option value="">— Bitte wählen —</option>
                          {f.options?.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      )}
                      {f.type === 'rating' && (
                        <RatingInput value={fieldData[f.key] || 0} onChange={v => setField(f.key, v)} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <button style={btnSecondary} onClick={() => save('draft')}><Save size={14} /> Als Entwurf speichern</button>
            <button style={btnPrimary} onClick={() => save('completed')}><Check size={14} /> Abschließen und ablegen</button>
            <button style={{ ...btnSecondary, marginLeft: 'auto' }} onClick={() => setView('list')}><X size={14} /> Abbrechen</button>
          </div>
        </div>
      </div>
    );
  }

  /* ---- List view ---- */
  return (
    <div>
      <div style={helpBox}>
        <strong>Formulare ausfüllen & ablegen:</strong> Hier füllen Sie standardisierte QM-Formulare aus — z.B. nach einer Coaching-Sitzung (Feedbackbogen),
        nach einem Audit (Audit-Checkliste) oder bei einer Beschwerde. Die ausgefüllten Formulare werden automatisch abgelegt und können als PDF exportiert werden.
        Klicken Sie auf „Neues Formular" und wählen Sie den passenden Typ.
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>{items.length} Einträge</span>
          <select style={{ ...inputStyle, width: 'auto', padding: '4px 8px', fontSize: '0.8rem' }} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">Alle Typen</option>
            <option value="feedback">Feedback</option>
            <option value="audit">Audit</option>
            <option value="complaint">Beschwerde</option>
          </select>
          <select style={{ ...inputStyle, width: 'auto', padding: '4px 8px', fontSize: '0.8rem' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Alle Status</option>
            <option value="draft">Entwurf</option>
            <option value="completed">Abgeschlossen</option>
            <option value="archived">Archiviert</option>
          </select>
        </div>
        <button style={btnPrimary} onClick={() => setView('choose')}><Plus size={14} /> Neues Formular</button>
      </div>

      {/* Grouped by category (folder style) */}
      {(() => {
        const types = filterType ? [filterType] : ['feedback', 'audit', 'complaint'];
        const grouped = types.map(type => ({
          type,
          label: templateTypeLabels[type] || type,
          color: templateTypeColors[type] || '#6b7280',
          icon: type === 'feedback' ? '📋' : type === 'audit' ? '🔍' : '⚠️',
          items: items.filter(i => i.template_type === type),
        }));
        return grouped.map(g => (
          <div key={g.type} style={{ marginBottom: 24 }}>
            <div
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 16px', background: g.color + '10', borderLeft: `4px solid ${g.color}`,
                borderRadius: 6, marginBottom: 8, cursor: 'pointer',
              }}
              onClick={() => startNew(g.type)}
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <FolderOpen size={18} style={{ color: g.color }} />
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{g.label}</span>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>({g.items.length})</span>
              </div>
              <button
                style={{ ...btnSecondary, padding: '4px 10px', fontSize: '0.8rem', background: g.color + '20', color: g.color, border: `1px solid ${g.color}40` }}
                onClick={e => { e.stopPropagation(); startNew(g.type); }}
              >
                <Plus size={12} /> Neu
              </button>
            </div>
            {g.items.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginLeft: 8 }}>
                <tbody>
                  {g.items.map(item => (
                    <tr
                      key={item.id}
                      style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                      onClick={() => openDetail(item)}
                      onMouseOver={e => (e.currentTarget.style.background = 'var(--bg)')}
                      onMouseOut={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={{ padding: '6px 12px', fontFamily: 'monospace', width: 120, color: 'var(--text-muted)' }}>{item.record_number}</td>
                      <td style={{ padding: '6px 12px', fontWeight: 500 }}>{item.title}</td>
                      <td style={{ padding: '6px 12px', width: 100 }}><StatusBadge status={item.status} /></td>
                      <td style={{ padding: '6px 12px', width: 90, color: 'var(--text-muted)' }}>{new Date(item.created_at).toLocaleDateString('de-DE')}</td>
                      <td style={{ padding: '6px 12px', textAlign: 'right', width: 140 }} onClick={e => e.stopPropagation()}>
                        <button style={{ ...btnSecondary, padding: '3px 6px' }} onClick={() => openDetail(item)} title="Öffnen"><Eye size={13} /></button>
                        <button style={{ ...btnSecondary, padding: '3px 6px', marginLeft: 3 }} onClick={() => downloadPdf(item)} title="PDF"><Download size={13} /></button>
                        <button style={{ ...btnSecondary, padding: '3px 6px', marginLeft: 3 }} onClick={() => startEdit(item)} title="Bearbeiten"><Edit2 size={13} /></button>
                        <button style={{ ...btnSecondary, padding: '3px 6px', marginLeft: 3 }} onClick={() => del(item.id)} title="Löschen"><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '8px 20px', fontStyle: 'italic' }}>Noch keine Einträge. Klicke auf "Neu" um ein Formular auszufüllen.</p>
            )}
          </div>
        ));
      })()}
    </div>
  );
}
